import {
  ActionCreator,
  createSlice,
  current,
  Dispatch,
  PayloadAction,
  ThunkAction,
} from "@reduxjs/toolkit";
import Interaction from "../../models/interactions/Interaction";
import { stateDeepCopy } from "../../helpers/DataUpdateHelper";
import NavHistoryNode from "../../models/ui/NavHistoryNode";
import { DatabaseTable } from "../../models/BaseTable";
import InteractionNode from "../../models/interactions/InteractionNode";

const testing = process.env.REACT_APP_TEST_MODE === "test";

export interface RightDrawerData {
  content: any;
  type: string;
  title?: string;
  resource?: typeof DatabaseTable;
  resourceName?: string;
  resourceID?: number;
  extraData?: any;
}

interface CurrentRootNavState {
  currentTab: string;
  rightDrawerOpen: boolean;
  rightDrawerData: Array<RightDrawerData>;
  breadcrumbs: Array<Interaction>;
  history: Array<Interaction>;
  errorMessage: null | string;
  isError: boolean;
  intervalID: number | null; // this is needed for interaction_parent_interval, if we need to create a new one need to know the corresponding interval
  currentParent: number | null; // this is needed for interaction_parent_interval, if we need to create a new one need to know the corresponding parent
  navHistory: Array<NavHistoryNode>;
  caresenseModalOpen: boolean;
  caresenseModalAlertType: string;
  caresenseModalAlertMessage: string;
  caresenseModalAlertEntityID: number | null;
  existingInteractionSearchType: string;
  previewMode: boolean; // turns off all crud, removes navbars and extra fluff for simple tree preview
  thisSet: number | null; // for interaction search, we need to store where we are
  thisParent: number | null; // for interaction search, we need to store where we are
  intervalSetID: number | null; // for interaction search, we need to store if we are adding an entity to an interval set matrix
  ordinal: number | null; // needed for inserting interactions at specific points
  modalCallback: any;
  selectedEntityID: any; //stores item that is selected from dropdown in search
  selectedEntity: any; //stores item that is selected from dropdown in search
  thisNode: InteractionNode | null
}

export const initialState: CurrentRootNavState = {
  currentTab: "",
  rightDrawerOpen: false,
  rightDrawerData: [],
  breadcrumbs: [],
  history: [],
  errorMessage: null,
  isError: false,
  intervalID: null, // for editing interaction_parent_interval comms
  currentParent: null, // for editing interaction_parent_interval comms
  navHistory: [],
  caresenseModalOpen: false,
  caresenseModalAlertType: "",
  caresenseModalAlertMessage: "",
  caresenseModalAlertEntityID: null,
  existingInteractionSearchType: "",
  previewMode: false,
  thisSet: null,
  thisParent: null,
  intervalSetID: null,
  ordinal: null,
  modalCallback: null,
  selectedEntityID: null,
  selectedEntity: null,
  thisNode: null
};

const navSlice = createSlice({
  name: "nav",
  initialState,
  reducers: {
    updateSlice(state, action: PayloadAction<Partial<CurrentRootNavState>>) {
      // Annoying bit of casting because WritatableDraft throws so many ts errors
      const stateData = state as unknown as CurrentRootNavState;
      let key: keyof CurrentRootNavState;

      for (key in action.payload) {
        (stateData[key] as any) = action.payload[key];
      }
      return stateData;
    },
    setThisNode(state, action: PayloadAction<any>) {
      state.thisNode = action.payload.thisNode;
    },
    setintervalSetID(state, action: PayloadAction<any>) {
      state.intervalSetID = action.payload.intervalSetID;
    },
    setSelectedEntityID(state, action: PayloadAction<any>) {
      state.selectedEntityID = action.payload.selectedEntityID;
    },
    setSelectedEntity(state, action: PayloadAction<any>) {
      state.selectedEntity = action.payload.selectedEntity;
    },
    turnPreviewModeOn(state) {
      state.previewMode = true;
    },
    turnPreviewModeOff(state) {
      state.previewMode = false;
    },
    addToHistory(state, action: PayloadAction<any>) {
      const newInteractionForHistory = action.payload.newInteractionForHistory;
      state.history.push(newInteractionForHistory);
      // console.log(current(state));
    },
    clearHistory(state) {
      state.history = [];
    },
    addToNavHistory(state, action: PayloadAction<any>) {
      const navHistoryNode: NavHistoryNode = action.payload.navHistoryNode;
      const navLength: number = action.payload.navLength;
      if (state.navHistory.length < navLength) {
        state.navHistory.push(navHistoryNode);
      }
    },
    setNavHistory(state, action: PayloadAction<any>) {
      let navHistory: Array<NavHistoryNode> = action.payload.navHistory;
      navHistory = stateDeepCopy(navHistory);
      // console.log(navHistory);

      // array should be reversed, and we need to add IDs
      // console.log(typeof navHistory);
      if (navHistory.length > 0) {
        const reversedNavHistoryNodes = navHistory.reverse();
        for (let i = 0; i < reversedNavHistoryNodes.length; i++) {
          reversedNavHistoryNodes[i].id = i;
        }
        state.navHistory = reversedNavHistoryNodes;
      } else {
        state.navHistory = navHistory;
      }
    },
    clearNavHistory(state) {
      state.navHistory = [];
    },
    // update the current top level NavBar tab - Content/Conditions vs Scheduling
    updateTab(state, action: PayloadAction<string>) {
      const tab = action.payload;
      state.currentTab = tab;
    },
    // set error message
    setError(state, action: PayloadAction<any>) {
      const error = action.payload.errorMessage;
      state.isError = action.payload.isError;
      state.errorMessage = error;
    },
    setCurrentParent(state, action: PayloadAction<any>) {
      const currentParent = action.payload.currentParent;
      state.currentParent = currentParent;
    },
    setIntervalID(state, action: PayloadAction<any>) {
      const intervalID = action.payload.intervalID;
      state.intervalID = intervalID;
    },
    // track interaction tree state for breadcrumbs
    addToBreadcrumbs(state, action: PayloadAction<any>) {
      const currentCrumb = action.payload.currentCrumb;
      state.breadcrumbs.push(currentCrumb);
      // console.log(current(state));
    },
    // go back to previous element in breadcrumbs
    goBackInBreadcrumbs(state) {
      const stateBreadcrumsCopy = stateDeepCopy(state.breadcrumbs);
      stateBreadcrumsCopy.splice(stateBreadcrumsCopy.length - 1, Infinity);
      state.breadcrumbs = stateBreadcrumsCopy;
    },
    // removes interactions elements off of the end of the breadcrumbs array
    // result is removal of all breadcrumbs after the selected one
    setBreadcrumbsToCurrentInteraction(state, action: PayloadAction<any>) {
      const interactionID = action.payload.interactionID;
      const stateBreadcrumsCopy = stateDeepCopy(state.breadcrumbs);
      const indexOfThisInteraction = stateBreadcrumsCopy.findIndex(
        (breadcrumb: Interaction) => {
          return breadcrumb.data.id === interactionID;
        },
      );
      stateBreadcrumsCopy.splice(indexOfThisInteraction + 1, Infinity);
      state.breadcrumbs = stateBreadcrumsCopy;
    },
    removeFromBreadcrumbs(state) {
      state.breadcrumbs.pop();
    },
    clearBreadcrumbs(state) {
      state.breadcrumbs = [];
    },
    // open another instance of right drawer
    openRightDrawer(state, action: PayloadAction<any>) {
      state.rightDrawerData.push({
        content: action.payload.content,
        type: action.payload.type,
        resourceID: action.payload.resourceID,
        resourceName: action.payload.resourceName,
        resource: action.payload.resource,
        extraData: action.payload.extraData,
      });
      state.rightDrawerOpen = true;
    },
    // close right drawer that's on top
    closeRightDrawer(state, action: PayloadAction<any>) {
      state.rightDrawerData.pop();
      state.rightDrawerOpen = state.rightDrawerData.length > 0;
    },
    openModal(state, action: PayloadAction<any>) {
      state.thisSet = action.payload.thisSet;
      state.thisParent = action.payload.thisParent;
      state.caresenseModalOpen = true;
      state.caresenseModalAlertType = action.payload.caresenseModalAlertType;
      state.existingInteractionSearchType = action.payload.type;
      if (action.payload.intervalSetID) {
        state.intervalSetID = action.payload.intervalSetID;
      }
      if (action.payload.intervalID) {
        state.intervalID = action.payload.intervalID;
      }
      state.ordinal = action.payload.ordinal;
      state.caresenseModalAlertMessage =
        action.payload.caresenseModalAlertMessage;
      state.caresenseModalAlertEntityID =
        action.payload.caresenseModalAlertEntityID;
      state.modalCallback = action.payload.modalCallback;
    },
    closeModal(state) {
      state.thisSet = null;
      state.thisParent = null;
      state.caresenseModalOpen = false;
      state.caresenseModalAlertType = "";
      state.existingInteractionSearchType = "";
      state.intervalSetID = null;
      state.modalCallback = null;
    },
  },
});

export const navSliceActions = navSlice.actions;

export default navSlice;

/* ACTION CREATORS/THUNKS */

export const setNavigationHistoryFromURL: ActionCreator<
  ThunkAction<any, any, any, any> // there are a LOT of types to account for, so just run this "open" with "any"
> = (params: string) => {
  return async (dispatch: Dispatch<any>): Promise<void> => {
    dispatch(navSliceActions.clearNavHistory());
    const payload = { input_string: params };
    const url =
      process.env.REACT_APP_API_URL + "/rpc/get_interactions_for_nav_history";
    try {
      fetch(url, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
          Authorization: "Bearer " + process.env.REACT_APP_POSTGREST_JWT,
          "Content-Type": "application/json",
          "Content-Profile": testing ? "test" : "build",
        },
      }).then(async (data: any) => {
        data = await data.json();
        for (let i = 0; i < data.length; i++) {
          if (
            typeof data[i] !== "undefined" &&
            typeof data[i].setid === "string" &&
            data[i].setid !== ""
          ) {
            if (data[i].setid === "") {
              data[i].setid = null;
            } else {
              data[i].setid = parseInt(data[i].setid);
            }
          }
          if (
            typeof data[i] !== "undefined" &&
            typeof data[i].parentid === "string"
          ) {
            if (data[i].parentid === "") {
              data[i].parentid = null;
            } else {
              data[i].parentid = parseInt(data[i].parentid);
            }
          }
        }
        dispatch(navSliceActions.setNavHistory({ navHistory: data }));
      });
    } catch (err: any) {
      console.error("Error with pulling nav history: " + err);
    }
  };
};
